import { useMemo, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, User, X, Plus, Loader2, Lock, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAllTeams } from '@/hooks/useAllTeams';
import { useModelGrants, useAddModelGrant, useRemoveModelGrant, useAllProfiles } from '@/hooks/useModelGrants';

interface Props {
  modelId: string;
  accessLevel: 'public' | 'restricted';
  onAccessLevelChange: (level: 'public' | 'restricted') => void;
}

export function ModelAccessManager({ modelId, accessLevel, onAccessLevelChange }: Props) {
  const isRestricted = accessLevel === 'restricted';
  const { data: grants = [], isLoading } = useModelGrants(isRestricted ? modelId : undefined);
  const addGrant = useAddModelGrant();
  const removeGrant = useRemoveModelGrant();

  const teamGrants = grants.filter(g => g.grantee_type === 'team');
  const userGrants = grants.filter(g => g.grantee_type === 'user');

  const handleRemove = (id: string) => {
    removeGrant.mutate({ id, modelId }, {
      onSuccess: () => toast.success('Acesso removido'),
      onError: (e: any) => toast.error(e.message),
    });
  };

  const handleAddTeam = (teamId: string) => {
    addGrant.mutate({ modelId, granteeType: 'team', teamId }, {
      onSuccess: () => toast.success('Time liberado'),
      onError: (e: any) => toast.error(e.message),
    });
  };

  const handleAddUser = (userId: string) => {
    addGrant.mutate({ modelId, granteeType: 'user', userId }, {
      onSuccess: () => toast.success('Usuário liberado'),
      onError: (e: any) => toast.error(e.message),
    });
  };

  return (
    <div className="space-y-3 rounded-md border border-color p-3">
      <div className="flex items-center gap-2">
        <Switch
          checked={isRestricted}
          onCheckedChange={(v) => onAccessLevelChange(v ? 'restricted' : 'public')}
        />
        <Label className="text-sm flex items-center gap-1.5">
          {isRestricted ? <Lock size={14} className="text-amber-500" /> : <Globe size={14} className="text-tertiary" />}
          Restringir acesso
        </Label>
      </div>

      {!isRestricted ? (
        <p className="text-xs text-tertiary pl-1">
          Modelo público — todos os usuários autenticados poderão usá-lo.
        </p>
      ) : (
        <div className="space-y-3 pl-1">
          <p className="text-xs text-tertiary">
            Apenas administradores e os times/usuários abaixo poderão ver e usar este modelo.
          </p>

          {isLoading ? (
            <div className="flex justify-center py-2"><Loader2 className="animate-spin text-tertiary" size={16} /></div>
          ) : (
            <>
              <GranteeSection
                title="Times liberados"
                icon={<Users size={13} />}
                grants={teamGrants}
                onRemove={handleRemove}
                addPicker={<TeamPicker existingTeamIds={new Set(teamGrants.map(g => g.team_id!))} onAdd={handleAddTeam} />}
              />
              <GranteeSection
                title="Usuários liberados"
                icon={<User size={13} />}
                grants={userGrants}
                onRemove={handleRemove}
                addPicker={<UserPicker existingUserIds={new Set(userGrants.map(g => g.user_id!))} onAdd={handleAddUser} />}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GranteeSection({
  title, icon, grants, onRemove, addPicker,
}: {
  title: string;
  icon: React.ReactNode;
  grants: { id: string; team_id: string | null; user_id: string | null }[];
  onRemove: (id: string) => void;
  addPicker: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-secondary flex items-center gap-1.5">{icon} {title}</span>
        {addPicker}
      </div>
      {grants.length === 0 ? (
        <p className="text-xs text-tertiary italic">Nenhum.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {grants.map(g => (
            <GranteeChip key={g.id} grant={g} onRemove={() => onRemove(g.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function GranteeChip({ grant, onRemove }: { grant: { team_id: string | null; user_id: string | null }; onRemove: () => void }) {
  const { data: teams = [] } = useAllTeams();
  const { data: users = [] } = useAllProfiles();
  const label = grant.team_id
    ? teams.find(t => t.id === grant.team_id)?.name ?? 'Time desconhecido'
    : users.find(u => u.id === grant.user_id)?.nome ?? users.find(u => u.id === grant.user_id)?.email ?? 'Usuário desconhecido';

  return (
    <Badge variant="secondary" className="gap-1 pr-1 text-xs">
      {label}
      <button type="button" onClick={onRemove} className="hover:text-red-500 transition-colors">
        <X size={11} />
      </button>
    </Badge>
  );
}

function TeamPicker({ existingTeamIds, onAdd }: { existingTeamIds: Set<string>; onAdd: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: teams = [], isLoading } = useAllTeams();
  const filtered = useMemo(() =>
    teams.filter(t => !existingTeamIds.has(t.id) && t.name.toLowerCase().includes(search.toLowerCase())),
    [teams, existingTeamIds, search]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs gap-1">
          <Plus size={12} /> Adicionar
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="end">
        <Input
          placeholder="Buscar time..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-8 text-sm mb-2"
        />
        <ScrollArea className="max-h-56">
          {isLoading ? (
            <div className="flex justify-center py-3"><Loader2 size={14} className="animate-spin text-tertiary" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-tertiary text-center py-3">Nenhum time disponível</p>
          ) : (
            <div className="space-y-0.5">
              {filtered.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { onAdd(t.id); setOpen(false); setSearch(''); }}
                  className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-hover text-primary"
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function UserPicker({ existingUserIds, onAdd }: { existingUserIds: Set<string>; onAdd: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: users = [], isLoading } = useAllProfiles();
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users
      .filter(u => !existingUserIds.has(u.id))
      .filter(u => !q || (u.nome?.toLowerCase().includes(q) ?? false) || u.email.toLowerCase().includes(q))
      .slice(0, 50);
  }, [users, existingUserIds, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs gap-1">
          <Plus size={12} /> Adicionar
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end">
        <Input
          placeholder="Buscar nome ou email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-8 text-sm mb-2"
        />
        <ScrollArea className="max-h-64">
          {isLoading ? (
            <div className="flex justify-center py-3"><Loader2 size={14} className="animate-spin text-tertiary" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-tertiary text-center py-3">Nenhum usuário encontrado</p>
          ) : (
            <div className="space-y-0.5">
              {filtered.map(u => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => { onAdd(u.id); setOpen(false); setSearch(''); }}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-hover"
                >
                  <div className="text-sm text-primary truncate">{u.nome ?? u.email}</div>
                  {u.nome && <div className="text-xs text-tertiary truncate">{u.email}</div>}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
